import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Users,
  UserPlus,
  Clock,
  CreditCard,
  Link2,
  Bot,
  Hash,
  Activity,
  Calendar,
  LogIn,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchAdminMetrics,
  fetchAdminUsers,
  AdminMetricsSnapshot,
  AdminUserRow,
  AdminUserSortColumn,
  AdminUserSortDir,
} from '../services/adminService';
import { formatDate } from '../utils/helpers';
import { formatSubscriptionStatus } from '../lib/subscription';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import './AdminPage.css';

function formatAdminSubscriptionStatus(status: string | null): string {
  if (!status) return 'No subscription';
  return formatSubscriptionStatus(status);
}

const PAGE_SIZE = 50;

const USER_TABLE_COLUMNS: Array<{ key: AdminUserSortColumn; label: string }> = [
  { key: 'email', label: 'Email' },
  { key: 'created_at', label: 'Signed up' },
  { key: 'last_sign_in_at', label: 'Last login' },
  { key: 'subscription_status', label: 'Subscription' },
  { key: 'subscription_current_period_end', label: 'Period end' },
  { key: 'betfair_connected', label: 'Betfair' },
  { key: 'ai_mode', label: 'Autopilot' },
  { key: 'bets_7d', label: 'Bets (7d)' },
  { key: 'staked_7d', label: 'Staked (7d)' },
  { key: 'net_pl_7d', label: 'P/L (7d)' },
  { key: 'last_bet_at', label: 'Last bet' },
];

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatGbp(amount: number): string {
  const sign = amount >= 0 ? '' : '-';
  return `${sign}£${Math.abs(amount).toFixed(2)}`;
}

function plTileClass(value: number): ' positive' | ' loss' | '' {
  if (value > 0) return ' positive';
  if (value < 0) return ' loss';
  return '';
}

const AdminPage: React.FC = () => {
  const { session } = useAuth();
  const accessToken = session?.access_token ?? '';

  const [metrics, setMetrics] = useState<AdminMetricsSnapshot | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [emailSearch, setEmailSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<AdminUserSortColumn>('created_at');
  const [sortDir, setSortDir] = useState<AdminUserSortDir>('desc');
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMetrics = useCallback(async () => {
    if (!accessToken) return;
    const data = await fetchAdminMetrics(accessToken);
    setMetrics(data);
  }, [accessToken]);

  const loadUsers = useCallback(
    async (
      targetPage: number,
      search: string,
      sort: AdminUserSortColumn,
      order: AdminUserSortDir
    ) => {
      if (!accessToken) return;
      setUsersLoading(true);
      try {
        const data = await fetchAdminUsers(accessToken, {
          page: targetPage,
          limit: PAGE_SIZE,
          search: search || undefined,
          sort,
          order,
        });
        setUsers(data.users);
        setTotalUsers(data.total);
        setPage(data.page);
      } finally {
        setUsersLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(emailSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [emailSearch]);

  useEffect(() => {
    const load = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await loadMetrics();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [accessToken, loadMetrics]);

  useEffect(() => {
    if (!accessToken || loading) return;
    void loadUsers(1, debouncedSearch, sortColumn, sortDir);
  }, [debouncedSearch, sortColumn, sortDir, accessToken, loading, loadUsers]);

  const signupChartData = useMemo(
    () =>
      (metrics?.trends.signupsByDay ?? []).map((point) => ({
        date: formatShortDate(point.date),
        signups: point.count ?? 0,
      })),
    [metrics?.trends.signupsByDay]
  );

  const betsChartData = useMemo(
    () =>
      (metrics?.trends.betsByDay ?? []).map((point) => ({
        date: formatShortDate(point.date),
        total: point.total ?? 0,
        ai: point.ai ?? 0,
      })),
    [metrics?.trends.betsByDay]
  );

  const totalPages = Math.max(1, Math.ceil(totalUsers / PAGE_SIZE));

  const handlePrevPage = () => {
    if (page <= 1 || usersLoading) return;
    void loadUsers(page - 1, debouncedSearch, sortColumn, sortDir);
  };

  const handleNextPage = () => {
    if (page >= totalPages || usersLoading) return;
    void loadUsers(page + 1, debouncedSearch, sortColumn, sortDir);
  };

  const handleSort = (column: AdminUserSortColumn) => {
    if (sortColumn === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDir(column === 'email' || column === 'subscription_status' ? 'asc' : 'desc');
  };

  const renderSortIcon = (column: AdminUserSortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="admin-table-sort-icon muted" aria-hidden />;
    }
    return sortDir === 'asc' ? (
      <ArrowUp size={14} className="admin-table-sort-icon" aria-hidden />
    ) : (
      <ArrowDown size={14} className="admin-table-sort-icon" aria-hidden />
    );
  };

  if (loading) {
    return (
      <div className="admin-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="admin-page">
        <ErrorMessage message={error ?? 'No admin data available.'} />
      </div>
    );
  }

  const manualBetsToday = Math.max(0, metrics.bets.today - metrics.bets.todayAi);

  const todayTiles = [
    { label: 'Signups today', value: metrics.users.signupsToday, icon: UserPlus },
    { label: 'Logins today', value: metrics.users.loginsToday, icon: LogIn },
    { label: 'Bets today', value: metrics.bets.today, icon: Hash },
    { label: 'Manual bets today', value: manualBetsToday, icon: Hash },
    { label: 'AI bets today', value: metrics.bets.todayAi, icon: Bot },
    { label: 'Staked today', display: formatGbp(metrics.volume.stakedToday), icon: DollarSign },
    {
      label: 'Net P/L today',
      display: formatGbp(metrics.volume.netPlToday),
      icon: metrics.volume.netPlToday >= 0 ? TrendingUp : TrendingDown,
      className: plTileClass(metrics.volume.netPlToday),
    },
    { label: 'Betfair connected today', value: metrics.betfair.connectedToday, icon: Link2 },
  ];

  const overviewTiles = [
    { label: 'Total users', value: metrics.users.total, icon: Users },
    { label: 'Signups (7d)', value: metrics.users.signups7d, icon: UserPlus },
    { label: 'On trial', value: metrics.subscriptions.trialing, icon: Clock },
    { label: 'Paying', value: metrics.subscriptions.active, icon: CreditCard, positive: true },
    { label: 'Betfair connected', value: metrics.betfair.connected, icon: Link2 },
    { label: 'Autopilot on', value: metrics.betfair.autopilotOn, icon: Bot, positive: true },
    { label: 'Bets (7d)', value: metrics.bets.last7d, icon: Hash },
    { label: 'AI bets (7d)', value: metrics.bets.last7dAi, icon: Bot },
    { label: 'Staked (7d)', display: formatGbp(metrics.volume.staked7d), icon: DollarSign },
    {
      label: 'Net P/L (7d)',
      display: formatGbp(metrics.volume.netPl7d),
      icon: metrics.volume.netPl7d >= 0 ? TrendingUp : TrendingDown,
      className: plTileClass(metrics.volume.netPl7d),
    },
    { label: 'WAU', value: metrics.users.wau, icon: Activity, positive: true },
    { label: 'MAU', value: metrics.users.mau, icon: Activity },
    { label: 'Track record requests (7d)', value: metrics.marketing.trackRecordRequests7d, icon: Activity },
    { label: 'Track record emails (7d)', value: metrics.marketing.trackRecordEmails7d, icon: Activity },
  ];

  const renderTiles = (
    tiles: Array<{
      label: string;
      value?: number;
      display?: string;
      icon: typeof Users;
      positive?: boolean;
      className?: string;
    }>
  ) =>
    tiles.map((tile) => {
      const Icon = tile.icon;
      const extraClass = tile.className ?? (tile.positive ? ' positive' : '');
      return (
        <div key={tile.label} className={`admin-tile${extraClass}`}>
          <div className="admin-tile-icon">
            <Icon size={24} />
          </div>
          <div className="admin-tile-value">{tile.display ?? tile.value}</div>
          <div className="admin-tile-label">{tile.label}</div>
        </div>
      );
    });

  return (
    <div className="admin-page">
      <Helmet>
        <title>Admin Analytics · GallopAI</title>
      </Helmet>

      <div className="admin-header">
        <div className="admin-header-top">
          <span className="admin-eyebrow">Operations</span>
        </div>
        <h1>Admin Analytics</h1>
        <p>
          Growth and usage overview for GallopAI. For revenue and billing detail, see the{' '}
          <a
            href="https://dashboard.stripe.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Stripe Dashboard
          </a>
          .
        </p>
      </div>

      <div className="admin-section">
        <h2 className="admin-section-title">
          <Calendar size={18} className="admin-section-icon" aria-hidden />
          Today (UTC)
        </h2>
        <div className="admin-tiles">{renderTiles(todayTiles)}</div>
      </div>

      <div className="admin-section">
        <h2 className="admin-section-title">Overview</h2>
        <div className="admin-tiles">{renderTiles(overviewTiles)}</div>
        <p className="admin-meta">
          Signups (30d): {metrics.users.signups30d} · Pending guest checkouts:{' '}
          {metrics.subscriptions.pendingGuestCheckouts} · Past due: {metrics.subscriptions.pastDue} ·
          Canceled: {metrics.subscriptions.canceled} ·{' '}
          <span title="Weekly Active Users: signed in or placed a bet in the last 7 days">
            WAU = weekly active users
          </span>
          {' · '}
          <span title="Monthly Active Users: signed in or placed a bet in the last 30 days">
            MAU = monthly active users
          </span>
          {' · '}
          P/L excludes pending/unmatched bets; platform P/L uses settled_at (UTC). Per-user P/L (7d) uses bets placed in the last 7 days.
        </p>
      </div>

      {(signupChartData.length > 0 || betsChartData.length > 0) && (
        <div className="admin-section">
          <h2 className="admin-section-title">Trends (30 days)</h2>
          <div className="admin-charts-section">
            <div className="admin-chart-container">
              <h3 className="admin-chart-title">Signups</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={signupChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis
                    dataKey="date"
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '6px',
                      color: '#f1f5f9',
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                  <Line
                    type="monotone"
                    dataKey="signups"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 3 }}
                    name="Signups"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="admin-chart-container">
              <h3 className="admin-chart-title">Bets placed</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={betsChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis
                    dataKey="date"
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '6px',
                      color: '#f1f5f9',
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 3 }}
                    name="Total bets"
                  />
                  <Line
                    type="monotone"
                    dataKey="ai"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', r: 3 }}
                    name="AI bets"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="admin-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title admin-section-title--inline">Users</h2>
          <div className="admin-users-search">
            <Search size={16} className="admin-users-search-icon" aria-hidden />
            <input
              type="search"
              className="admin-users-search-input"
              placeholder="Search by email…"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              aria-label="Search users by email"
            />
          </div>
        </div>
        <div className={`admin-table-container${usersLoading ? ' is-loading' : ''}`}>
          {usersLoading && users.length === 0 ? (
            <LoadingSpinner />
          ) : (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    {USER_TABLE_COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        aria-sort={
                          sortColumn === column.key
                            ? sortDir === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          className={`admin-table-sort${sortColumn === column.key ? ' active' : ''}`}
                          onClick={() => handleSort(column.key)}
                        >
                          <span>{column.label}</span>
                          {renderSortIcon(column.key)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((row) => (
                    <tr key={row.user_id}>
                      <td>{row.email ?? '—'}</td>
                      <td>{formatDate(row.created_at)}</td>
                      <td>{formatDateTime(row.last_sign_in_at)}</td>
                      <td>
                        <span className="admin-badge">
                          {formatAdminSubscriptionStatus(row.subscription_status)}
                        </span>
                      </td>
                      <td>
                        {row.subscription_current_period_end
                          ? formatDate(row.subscription_current_period_end)
                          : '—'}
                      </td>
                      <td>
                        <span className={`admin-badge ${row.betfair_connected ? 'yes' : 'no'}`}>
                          {row.betfair_connected ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <span className={`admin-badge ${row.ai_mode ? 'yes' : 'no'}`}>
                          {row.ai_mode ? 'On' : 'Off'}
                        </span>
                      </td>
                      <td>{row.bets_7d}</td>
                      <td>{formatGbp(row.staked_7d)}</td>
                      <td className={row.net_pl_7d >= 0 ? 'admin-pl-positive' : row.net_pl_7d < 0 ? 'admin-pl-negative' : undefined}>
                        {formatGbp(row.net_pl_7d)}
                      </td>
                      <td>{formatDateTime(row.last_bet_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="admin-pagination">
                <span className="admin-pagination-info">
                  Page {page} of {totalPages} · {totalUsers} user{totalUsers === 1 ? '' : 's'}
                  {debouncedSearch ? ` matching “${debouncedSearch}”` : ''}
                </span>
                <div className="admin-pagination-actions">
                  <button
                    type="button"
                    className="admin-pagination-btn"
                    onClick={handlePrevPage}
                    disabled={page <= 1 || usersLoading}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="admin-pagination-btn"
                    onClick={handleNextPage}
                    disabled={page >= totalPages || usersLoading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        <p className="admin-meta">
          Snapshot generated at {formatDateTime(metrics.generatedAt)}
        </p>
      </div>
    </div>
  );
};

export default AdminPage;
